/**
 * Media Library (spec §43). Tenant-level reusable media: uploaded horse photos
 * and video, logos, sponsor logos, backgrounds, music, and the generated
 * outputs the studio itself produces. Media can be tenant-wide, auction-scoped
 * or lot-scoped. This declares the shape only; storage keys resolve through the
 * existing object-storage abstraction (spec §84, §96 - do not build another).
 */

import { z } from 'zod';

export const MEDIA_ASSET_TYPES = ['IMAGE', 'VIDEO', 'AUDIO', 'LOGO', 'DOCUMENT'] as const;
export const MediaAssetTypeSchema = z.enum(MEDIA_ASSET_TYPES);
export type MediaAssetType = z.infer<typeof MediaAssetTypeSchema>;

export const MEDIA_SOURCES = ['UPLOAD', 'AUCTION', 'LOT', 'GENERATED', 'SYSTEM'] as const;
export const MediaSourceSchema = z.enum(MEDIA_SOURCES);
export type MediaSource = z.infer<typeof MediaSourceSchema>;

export const MediaAssetSchema = z
  .object({
    id: z.string().min(1),
    tenantId: z.string().min(1),
    type: MediaAssetTypeSchema,
    source: MediaSourceSchema,
    mimeType: z.string().min(1),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    durationMs: z.number().int().positive().optional(),
    tags: z.array(z.string()).optional(),
    lotId: z.string().min(1).optional(),
    auctionId: z.string().min(1).optional(),
    /** Opaque key into the platform object store, never a public URL. */
    storageKey: z.string().min(1),
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type MediaAsset = z.infer<typeof MediaAssetSchema>;
